// Member status codes
export type MemberStatus =
  | 'p'  // Pledge
  | 'u'  // Undergraduate
  | 'a'  // Alumnus
  | 'l'  // Local Alumnus
  | 's'  // Sweetheart
  | 'd'  // Deceased
  | 'w'  // Widow
  | 'f'  // Expelled
  | 'o'  // Family
  | 'n'  // National Officer
  | 'c'  // Other Chapter
  | 'e'  // ERAU Officer
  | 'g'  // Volusia County Alumni
  | 'x'  // Abandoned
  | 'F'; // Eta Iota Founder

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  p: 'Pledge',
  u: 'Undergraduate',
  a: 'Alumnus',
  l: 'Local Alumnus',
  s: 'Sweetheart',
  d: 'Deceased',
  w: 'Widow',
  f: 'Expelled',
  o: 'Family',
  n: 'National Officer',
  c: 'Other Chapter',
  e: 'ERAU Officer',
  g: 'Volusia County Alumni',
  x: 'Abandoned',
  F: 'Eta Iota Founder',
};

export const ACTIVE_STATUSES: MemberStatus[] = ['p', 'u', 'a', 'l', 'n', 'g', 'F'];

// Office codes — synced with EtaIota-Prod-ReferenceData (REFDATA#office)
export const OFFICE_LABELS: Record<string, string> = {
  // Chapter officers
  '1': 'Consul', '2': 'Pro Consul', '3': 'Annotator', '4': 'Queastor',
  '5': 'Tribune', '6': 'Magister', '7': 'Rush Chairman', '8': 'House Manager',
  '9': 'Social Chairman', 'A': 'IFC Rep', 'B': 'Pool Manager',
  'C': 'Peterson Chairman', 'D': 'Scholarship Chairman', 'E': 'Athletic Chairman',
  'F': 'Chapter Webmaster', 'G': 'Other Undergraduate', 'H': 'In-house Chef',
  'I': 'Asst. Queastor',
  'AR': 'Alumni Relations Chairman',
  // Chapter / alumni roles
  'a': 'Brotherhood Chair', 'b': 'Historian', 'c': 'Chapter Editor',
  'd': 'Risk Manager', 'e': 'Kustos', 'f': 'Fund Raising',
  'g': 'Birthday Chairman', 'q': 'Chapter Advisor',
  'r': 'Volusia County Alumni Chapter Officer',
  // House Corp
  'h': 'House Corp President', 'i': 'House Corp Vice President',
  'j': 'House Corp Secretary', 'k': 'House Corp Treasurer',
  'l': 'House Corp Fund raising', 'm': 'House Corp Database Admin',
  'n': 'etaiota.org Webmaster', 'o': '1855 Club Chairman',
  'p': 'Florida Province Grand Praetor', 's': 'House Corp Trustee',
  'T': 'House Corp Tech Advisor', 'v': 'Housing Committee Member',
  'x': 'RMF Chairman', 'y': 'System Administrator / HI Task Tracker',
  'z': 'Birthday Coordinator',
  // National officers
  'GC': 'Grand Consul', 'GP': 'Grand Pro Consul', 'GQ': 'Grand Quaestor',
  'GH': 'Grand Historian', 'GT': 'Grand Tribune',
  'gp': 'Grand Praetor', 'gt': 'Grand Trustee',
};

// Officer position categories for better organization
export const UNDERGRADUATE_OFFICES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'a', 'b', 'c', 'd', 'e', 'f', 'g'];
export const HOUSE_CORP_OFFICES = ['h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 'v', 'x', 'y', 'z'];
export const NATIONAL_OFFICES: string[] = [];

// 1855 Club levels
export type Club1855Level = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '9';

export const CLUB_1855_LABELS: Record<Club1855Level, string> = {
  '0': 'Non Member',
  '1': 'Blue',
  '2': 'Gold',
  '3': 'White Cross',
  '4': 'Norman Shield',
  '5': 'Founder',
  '6': 'Constantine',
  '7': 'Lockwood',
  '9': 'Past Contributor',
};

// Tracks *how* a member is currently giving, separate from club1855Level —
// see backend/shared/types.ts and docs/1855-club-stripe-plan.md.
export type Club1855GivingSource =
  | '1855_paypal_active'
  | '1855_stripe_active'
  | '1855_migrated'
  | '1855_lapsed';

// User roles
export type UserRole = 'member' | 'admin' | 'superadmin';

// Member interface
export interface Member {
  memberId: number;
  rollbookNumber?: number | null;

  personal: {
    firstName: string;
    lastName: string;
    suffix?: string;
    nickname?: string;
    email?: string;
    graduationYear?: number;
  };

  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };

  contact?: {
    homePhone?: string;
    workPhone?: string;
    cellPhone?: string;
  };

  employment?: {
    employer?: string;
    jobTitle?: string;
  };

  sigmaChi: {
    status: MemberStatus;
    pledgeClass?: string;
    initiationDate?: string | null;
    birthDate?: string | null;
    club1855Level?: Club1855Level;
    club1855GivingSource?: Club1855GivingSource;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    affiliateInitiate?: string;
    initiatingChapter?: string;  // Set if initiated at a different chapter (not Eta Iota)
    offices?: string[];
    currentOffices?: string[];
    pastOffices?: string[];
    lifeLoyalSig?: boolean;
    orderOfConstantine?: boolean;
    significantSig?: boolean;
    bigBrotherId?: number | null;
    officeTerms?: Record<string, number>;
    officeRegions?: Record<string, string>;
  };

  spouse?: {
    name?: string;
    anniversary?: string;
  };

  permanentContact?: {
    name?: string;
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    email?: string;
  };

  bio?: {
    hometown?: string;
    major?: string;
    hobbies?: string;
    quote?: string;
    activities?: string;
    interestTags?: string[];
  };

  social?: {
    website?: string;
    linkedin?: string;
  };

  permissions?: {
    // Member-requested: hide phone numbers and mailing address from other members.
    // Admins and the member's own view are unaffected. Photo and email are always visible.
    hideContactInfo?: boolean;
  };

  flags?: {
    paperNewsletter?: boolean;
    addressLost?: boolean;
    emailOptOut?: boolean;  // Single opt-out for all community emails
  };

  deceased?: boolean;
  comment?: string;
  adminNotes?: string;
  photoUrl?: string | null;

  eventRegistrations?: {
    [eventSlug: string]: {
      registered: boolean;
      registeredDate?: string;
      source?: string;
    };
  };

  metadata?: {
    createdAt: string;
    updatedAt: string;
    accountClaimedAt?: string;
    accountClaimed?: boolean;
    cognitoUserId?: string;
    emailStatus?: string;
    emailBounceSource?: string;
    bounceTimestamp?: string;
    profileReviewedAt?: string;
  };

  // GSI attributes (denormalized for querying)
  status?: MemberStatus;
  graduationYear?: number;
  pledgeClass?: string;
  state?: string;
  lastName?: string;
  fullName?: string;
  email?: string;

  // Cognito role (populated for admin user management)
  role?: string;
}

// API response types
export interface SearchMembersResponse {
  members: Member[];
  count: number;
  lastKey?: string;
}

// Email activity record
export interface EmailActivity {
  messageId: string;
  recipient: string;
  subject?: string;
  campaign?: string;
  emailType?: string;
  memberId?: number;
  memberName?: string;
  sentAt: string;
  status: 'sent' | 'delivered' | 'bounced' | 'complained';
  deliveredAt?: string;
  bouncedAt?: string;
  bounceType?: string;
  complainedAt?: string;
  opens?: number;
  firstOpenedAt?: string;
  lastOpenedAt?: string;
  clicks?: number;
  firstClickedAt?: string;
  lastClickedAt?: string;
  clickedLinks?: string[];
}

// Audit log entry
export interface AuditLogEntry {
  memberId: number;
  memberName?: string;
  changeType: 'create' | 'update' | 'delete' | 'claim';
  previousValues?: Partial<Member>;
  newValues?: Partial<Member>;
  changedBy: {
    type: 'member' | 'admin' | 'system' | 'migration';
    userId?: string;
    email?: string;
  };
  timestamp: string;
  notes?: string;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  memberName?: string;
  lastKey?: string;
}

// Donor transaction ledger (1855 Club and adjacent giving)
export type TransactionProgram = '1855_club' | 'brick' | 'getaroom' | 'other' | 'uncategorized';
export type TransactionType = 'recurring' | 'one_time';
export type TransactionInterval = 'monthly' | 'annual';
export type TransactionPaymentMethod = 'card' | 'ach' | 'paypal' | 'check' | 'cash' | 'other';
// Mirrors backend/shared/types.ts — keep both in sync.
export type TransactionSource = 'stripe' | 'paypal_import' | 'speedchex' | 'manual';
export type TransactionStatus = 'succeeded' | 'refunded' | 'failed';

export interface TransactionLedgerEntry {
  memberId: number;
  memberName?: string; // enriched by the admin endpoint only
  date: string;
  amount: number;
  fee?: number;
  net?: number;
  program: TransactionProgram;
  type: TransactionType;
  interval?: TransactionInterval;
  level?: string;
  paymentMethod: TransactionPaymentMethod;
  source: TransactionSource;
  externalId?: string;
  status: TransactionStatus;
  notes?: string;
  // Short Eta Iota-branded reference (e.g. "EI-3F2A-9C41") — safe to show any
  // member; derived server-side from the ledger row's own key, never stored.
  confirmationCode?: string;
  // Stripe-hosted receipt/invoice URL — Stripe-sourced rows only. Its
  // absence (paypal_import/manual rows) is what the table uses to hide the
  // receipt link entirely rather than show a dead one.
  receiptUrl?: string;
  // e.g. "Visa •••• 4242" or "Chase •••• 6789" — Stripe-sourced rows only.
  paymentMethodDetail?: string;
}

export interface TransactionsResponse {
  entries: TransactionLedgerEntry[];
  lastKey?: string;
}

export interface TransactionsSummary {
  count: number;
  totalAmount: number;
  totalFee: number;
  totalNet: number;
  failedCount: number;
  refundedCount: number;
}

export interface Club1855ReconciliationRow {
  memberId: number;
  name: string;
  currentLevel: Club1855Level;
  currentLevelLabel: string;
  lastGiftDate: string | null;
  lastGiftAmount: number | null;
  daysSinceLastGift: number | null;
  last12MonthsCount: number;
  last12MonthsTotal: number;
  lifetimeCount: number;
  lifetimeTotal: number;
  impliedLevel: string | null;
  flags: {
    lapsed: boolean;
    levelMismatch: boolean;
    uncredited: boolean;
    reactivationCandidate: boolean;
  };
}

export interface Club1855ReconciliationResponse {
  rows: Club1855ReconciliationRow[];
  summary: {
    totalMembers: number;
    byLevel: Record<string, number>;
    lapsedCount: number;
    levelMismatchCount: number;
    uncreditedCount: number;
    reactivationCandidateCount: number;
    generatedAt: string;
  };
}

export interface CreateTransactionRequest {
  memberId: number;
  date: string;
  amount: number;
  program: TransactionProgram;
  level?: string;
  paymentMethod: TransactionPaymentMethod;
  notes?: string;
}

// Batch member import types
export interface BatchMemberInput {
  firstName: string;
  lastName: string;
  email: string;
  status?: string;          // single-letter code (e.g. 'u') or human-readable label
  graduationYear?: number;
  pledgeClass?: string;
  rollbookNumber?: number;
  suffix?: string;
  nickname?: string;
  initiationDate?: string | null;  // YYYY-MM-DD (converted by frontend); null clears the field
  birthDate?: string | null;       // YYYY-MM-DD (converted by frontend); null clears the field
  cellPhone?: string;
}

export interface BatchMemberResult {
  rowIndex: number;
  success: boolean;
  memberId?: number;
  emailSent?: boolean;
  warning?: string;
  error?: string;
}

export interface BatchCreateResponse {
  results: BatchMemberResult[];
  created: number;
  failed: number;
  emailsSent: number;
}

// Sort configuration
export type SortField = 'lastName' | 'status' | 'graduationYear' | 'pledgeClass';
export type SortDirection = 'asc' | 'desc';
export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

// Search params
export interface SearchParams {
  query?: string;
  status?: MemberStatus | MemberStatus[];
  graduationYear?: number;
  pledgeClass?: string;
  state?: string;
  includeDeceased?: boolean;
  deceasedOnly?: boolean;
  excludeStatuses?: MemberStatus[];
  limit?: number;
  lastKey?: string;
}

// Normalize offices for backward compatibility with legacy `offices` field
export function normalizeOffices(sigmaChi?: Member['sigmaChi']): {
  currentOffices: string[];
  pastOffices: string[];
} {
  if (!sigmaChi) return { currentOffices: [], pastOffices: [] };
  if (sigmaChi.currentOffices || sigmaChi.pastOffices) {
    // Merge legacy offices array with pastOffices to avoid data loss for
    // members whose records were partially migrated to the new split format.
    const past = sigmaChi.pastOffices ?? [];
    const legacy = sigmaChi.offices ?? [];
    const merged = [...new Set([...past, ...legacy])];
    return {
      currentOffices: sigmaChi.currentOffices ?? [],
      pastOffices: merged,
    };
  }
  // Legacy fallback: treat old `offices` as past offices
  return {
    currentOffices: [],
    pastOffices: sigmaChi.offices || [],
  };
}

// User state
export interface User {
  id: string;
  email: string;
  memberId?: number;
  role: UserRole;
  groups: string[];
}
